/**
 * FieldRegistry - Dynamic field type mapping system
 * Maps backend field types to React components
 */

import React from 'react';
import { FormField } from '../../../types/forms';

// Import field components
import TextField from '../fields/TextField';
import SelectField from '../fields/SelectField';
import CheckboxField from '../fields/CheckboxField';
import DateField from '../fields/DateField';
import NumberField from '../fields/NumberField';
import TextAreaField from '../fields/TextAreaField';
import RadioField from '../fields/RadioField';
import SignatureField from '../fields/SignatureField';
import FileUploadField from '../fields/FileUploadField';

// Field component type
export type FieldComponent = React.ComponentType<any>;

// Registry class for managing field types
class FieldRegistryClass {
  private registry: Map<string, FieldComponent> = new Map();

  constructor() {
    // Register default field types
    this.registerDefaults();
  }

  /**
   * Register default field components
   */
  private registerDefaults(): void {
    // Text fields - all use TextField with different input types
    this.register('text', TextField);
    this.register('email', TextField);
    this.register('password', TextField);
    this.register('tel', TextField);
    this.register('phone', TextField);
    this.register('url', TextField);

    // Selection fields
    this.register('select', SelectField);
    this.register('multiselect', SelectField);
    this.register('checkbox', CheckboxField);
    this.register('radio', RadioField);

    // Date/time fields
    this.register('date', DateField);
    this.register('datetime', DateField);

    // Number fields
    this.register('number', NumberField);
    this.register('currency', NumberField);

    // Text area
    this.register('textarea', TextAreaField);

    // Special fields
    this.register('file', FileUploadField);
    this.register('signature', SignatureField);
  }

  /**
   * Register a custom field component
   */
  register(type: string, component: FieldComponent): void {
    if (!type || !component) {
      throw new Error('Field type and component are required');
    }
    this.registry.set(type, component);
  }

  /**
   * Unregister a field component
   */
  unregister(type: string): void {
    this.registry.delete(type);
  }

  /**
   * Get a field component by type
   */
  get(type: string): FieldComponent | undefined {
    return this.registry.get(type);
  }

  /**
   * Check if a field type is registered
   */
  has(type: string): boolean {
    return this.registry.has(type);
  }

  /**
   * Get all registered field types
   */
  getTypes(): string[] {
    return Array.from(this.registry.keys());
  }

  /**
   * Create field props from schema
   */
  createFieldProps(field: FormField, value: any, onChange: (value: any) => void): Record<string, any> {
    const baseProps = {
      name: field.code,
      label: field.label,
      placeholder: field.placeholder,
      helpText: field.helpText,
      required: field.validation?.required,
      disabled: false,
      readonly: field.readonly,
      value,
      onChange,
    };

    // Add type-specific props
    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'tel':
      case 'url':
        return {
          ...baseProps,
          maxLength: field.validation?.maxLength,
          pattern: field.validation?.pattern,
        };

      case 'number':
      case 'currency':
        return {
          ...baseProps,
          min: field.validation?.minValue,
          max: field.validation?.maxValue,
          precision: field.type === 'currency' ? 2 : undefined,
          thousandSeparator: field.type === 'currency',
          prefix: field.type === 'currency' ? '$' : undefined,
        };

      case 'select':
      case 'radio':
        return {
          ...baseProps,
          options: field.options || [],
          searchable: field.type === 'select',
          clearable: field.type === 'select',
        };

      case 'multiselect':
        return {
          ...baseProps,
          options: field.options || [],
          multiple: true,
          searchable: true,
          clearable: true,
        };

      case 'checkbox':
        return {
          ...baseProps,
          checked: value || false,
        };

      case 'date':
        return {
          ...baseProps,
          showTime: false,
        };

      case 'datetime':
        return {
          ...baseProps,
          showTime: true,
        };

      case 'textarea':
        return {
          ...baseProps,
          rows: 4,
          maxLength: field.validation?.maxLength,
          autoResize: true,
          showCount: true,
        };

      case 'file':
        return {
          ...baseProps,
          accept: field.validation?.allowedValues?.join(','),
          multiple: false,
          maxSize: 10 * 1024 * 1024, // 10MB default
          preview: true,
        };

      case 'signature':
        return {
          ...baseProps,
          width: 400,
          height: 200,
        };

      default:
        return baseProps;
    }
  }

  /**
   * Validate if all field types in a schema are supported
   */
  validateSchema(fields: FormField[]): { valid: boolean; unsupported: string[] } {
    const unsupported = fields
      .map((field) => field.type)
      .filter((type) => !this.has(type));

    return {
      valid: unsupported.length === 0,
      unsupported: Array.from(new Set(unsupported)),
    };
  }

  /**
   * Clone the registry
   */
  clone(): FieldRegistryClass {
    const cloned = new FieldRegistryClass();
    this.registry.forEach((component, type) => {
      cloned.register(type, component);
    });
    return cloned;
  }

  /**
   * Extend the registry with another registry
   */
  extend(other: FieldRegistryClass): void {
    other.registry.forEach((component, type) => {
      this.register(type, component);
    });
  }

  /**
   * Clear all registrations
   */
  clear(): void {
    this.registry.clear();
  }

  /**
   * Reset to default registrations
   */
  reset(): void {
    this.clear();
    this.registerDefaults();
  }
}

// Create and export singleton instance
export const FieldRegistry = new FieldRegistryClass();

// Export class for testing or custom instances
export default FieldRegistryClass;